/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'underscore',
	'../../../lib/@waca/upgrades/UpgradeBase',
	'dashboard-analytics/dataSources/services/DataSourcesService',
	'./utils/WidgetUpgradeUtils',
	'../../../app/nls/StringResources'
], (_, UpgradeBase, DataSourcesService, WidgetUpgradeUtils, StringResources) => {

	class FredIsRedColorMapUpgrade extends UpgradeBase {
		constructor() {
			super();

			this.VERSION = 1808;
		}

		/**
		 * Perform upgrade
		 *
		 * @param {object} spec - spec to perform upgrade on
		 *
		 * @return {Promise} Promise to be resolved when upgrade performed
		 */
		up(spec) {
			if (!spec) {
				return Promise.resolve(spec);
			}
			if (!WidgetUpgradeUtils.specHasWidgets(spec)) {
				return Promise.resolve(spec);
			}
			const promises = [];
			const shouldUpdateFredIsRedColorMap = this._hasFredIsRedColorMap(spec);
			if (shouldUpdateFredIsRedColorMap) {
				this._initializeSourcesCollection(spec);
				_.each(spec.widgets, model => {
					if (model && WidgetUpgradeUtils.isLiveWidgetModel(model)) {
						const promise = this._upgradeFredIsRedColorMap(model, spec.fredIsRed.colorMap);
						promises.push(promise);
					}
				});
			}
			return Promise.all(promises).then(() => spec);
		}

		// Downgrades are not available in CA
		down(spec) {
			return Promise.resolve(spec);
		}

		_initializeSourcesCollection(spec) {
			const dataSourcesClass = this._DataSourcesServices || DataSourcesService;
			this.dataSourcesService = new dataSourcesClass({
				features: {
					API: this.data.dashboardApi,
					Logger: this.data.logger
				}
			});
			this.dataSourcesService.initializeSourcesCollection(spec);
		}
		_upgradeAggregationType(model) {
			model.properties.push({
				id: 'widget.legend.aggregateTypeVisible',
				value: true
			});
		}

		_upgradeFredIsRedColorMap(model, fredIsRedColorMap) {
			const dataViews = model.data && model.data.dataViews;
			const modelRef = dataViews && dataViews[0] ? dataViews[0].modelRef : null;
			if (!dataViews || !modelRef) {
				return Promise.resolve();
			}
			const promise = this._getModule(modelRef).then(module => {
				_.each(dataViews, dataView => {
					this._addUnformattedValues(module, dataView, fredIsRedColorMap);
				});
			});
			return promise;
		}

		_addUnformattedValues(module, dataView, fredIsRedColorMap) {
			const dataItems = dataView.dataItems;
			if (dataItems && this._isMultiMeasuresSeries(dataItems)) {
				_.each(dataItems, dataItem => {
					this._addUnformattedValue(module, dataItem, fredIsRedColorMap);
				});
			}
		}

		_addUnformattedValue(module, dataItem, fredIsRedColorMap) {
			const mdc = module.getMetadataColumn(dataItem.itemId);
			if(mdc) {
				const aggregate = dataItem.aggregate || mdc.getDefaultAggregation();
				//It was too late to change the string for R7 and translations were checked that they are all the same format.
				const formattedValue = dataItem.itemLabel + ' (' + StringResources.get(aggregate) + ')';
				const unformattedValue = dataItem.itemLabel + aggregate;
				if (fredIsRedColorMap[formattedValue]) {
					fredIsRedColorMap[unformattedValue] = fredIsRedColorMap[formattedValue];
				}
			}
		}

		_isMultiMeasuresSeries(dataItems = []) {
			return dataItems.filter(item => item.itemId === '_multiMeasuresSeries').length > 0;
		}

		_hasFredIsRedColorMap(spec) {
			return spec.fredIsRed && !_.isEmpty(spec.fredIsRed.colorMap);
		}

		_getModule(modelRef) {
			return this.dataSourcesService.getModule(modelRef);
		}

	}

	return new FredIsRedColorMapUpgrade();
});